import { useState, type JSX, type ReactNode } from 'react';
import { useStore } from '@/state/store';
import { Panel } from '@/components/ui/Panel';
import { ASSIGNMENT_COLOR_DARK } from '@/origami/types';

interface Topic {
  id: string;
  title: string;
  body: ReactNode;
  figure?: ReactNode;
  /** Loads an example that demonstrates the idea. */
  example?: string;
}

const MV_FIGURE = (
  <svg width="200" height="76" viewBox="0 0 200 76">
    <line x1="14" y1="26" x2="90" y2="26" stroke={ASSIGNMENT_COLOR_DARK.M} strokeWidth="2.5" />
    <text x="14" y="16" fontSize="9" fill="var(--text-dim)">mountain (ρ &lt; 0)</text>
    <path d="M14 56 L52 40 L90 56" fill="none" stroke="var(--text-faint)" strokeWidth="1.2" />
    <line x1="110" y1="26" x2="186" y2="26" stroke={ASSIGNMENT_COLOR_DARK.V} strokeWidth="2.5" />
    <text x="110" y="16" fontSize="9" fill="var(--text-dim)">valley (ρ &gt; 0)</text>
    <path d="M110 40 L148 56 L186 40" fill="none" stroke="var(--text-faint)" strokeWidth="1.2" />
  </svg>
);

function KawasakiFigure(): JSX.Element {
  const r = 34;
  const cx = 100;
  const cy = 42;
  const angles = [0, 70, 180, 290];
  return (
    <svg width="200" height="88" viewBox="0 0 200 88">
      {angles.map((deg, i) => {
        const a = (deg * Math.PI) / 180;
        const next = ((angles[(i + 1) % angles.length] + (i === angles.length - 1 ? 360 : 0)) * Math.PI) / 180;
        const large = next - a > Math.PI ? 1 : 0;
        return (
          <path
            key={i}
            d={`M ${cx} ${cy} L ${cx + Math.cos(a) * r} ${cy - Math.sin(a) * r} A ${r} ${r} 0 ${large} 0 ${
              cx + Math.cos(next) * r
            } ${cy - Math.sin(next) * r} Z`}
            fill={i % 2 === 0 ? 'rgba(76,141,255,0.25)' : 'rgba(150,160,176,0.14)'}
          />
        );
      })}
      {angles.map((deg, i) => {
        const a = (deg * Math.PI) / 180;
        return (
          <line
            key={`l${i}`}
            x1={cx}
            y1={cy}
            x2={cx + Math.cos(a) * r}
            y2={cy - Math.sin(a) * r}
            stroke="var(--text)"
            strokeWidth="1.5"
          />
        );
      })}
      <text x="12" y="80" fontSize="9" fill="var(--text-dim)">
        blue sectors = 180°, grey sectors = 180°
      </text>
    </svg>
  );
}

const TOPICS: Topic[] = [
  {
    id: 'mv',
    title: 'Mountain vs valley',
    figure: MV_FIGURE,
    body: (
      <>
        <p>
          A crease is a <b>valley</b> if the two facets rotate toward you and a <b>mountain</b> if
          they rotate away. fold.app measures this as a signed dihedral angle ρ: zero when flat,
          positive for a valley, negative for a mountain, ±180° when fully folded.
        </p>
        <p>
          Turning the paper over swaps every mountain and every valley — the assignment is a property
          of the crease <i>and</i> which side you are looking from.
        </p>
      </>
    ),
    example: 'single-valley',
  },
  {
    id: 'cp',
    title: 'Crease pattern',
    body: (
      <>
        <p>
          A crease pattern is the flat sheet with every fold line drawn on it. fold.app stores it
          as a planar embedded graph: crossing creases are split at their intersection, and the faces
          between creases are derived from the half-edge structure rather than drawn by hand.
        </p>
        <p>
          That is why drawing one crease across another instantly increases the face count in the
          Pattern panel.
        </p>
      </>
    ),
  },
  {
    id: 'sector',
    title: 'Sector angle',
    body: (
      <>
        <p>
          The creases at a vertex divide the paper around it into <b>sectors</b>. Their angles, taken
          counter-clockwise, always sum to 360° at an interior vertex. Click any vertex to see the
          sector diagram.
        </p>
      </>
    ),
  },
  {
    id: 'kawasaki',
    title: "Kawasaki's theorem",
    figure: <KawasakiFigure />,
    body: (
      <>
        <p>
          At an interior vertex of degree 2n with sectors α₁ … α₂ₙ, the paper can fold flat only if
          the alternating sum vanishes:
        </p>
        <p>
          <code>α₁ − α₂ + α₃ − … − α₂ₙ = 0</code>
        </p>
        <p>
          equivalently the odd-indexed sectors and the even-indexed sectors each add to 180°. For a
          single vertex this is also <i>sufficient</i>. Odd-degree interior vertices can never fold
          flat.
        </p>
      </>
    ),
    example: 'four-crease-vertex',
  },
  {
    id: 'maekawa',
    title: "Maekawa's theorem",
    body: (
      <>
        <p>
          At every interior vertex of a flat-folded pattern, the numbers of mountain and valley
          creases differ by exactly two: <code>|M − V| = 2</code>.
        </p>
        <p>
          Two consequences fall straight out: the degree of such a vertex is even, and a four-fold
          symmetric pattern can never have a four-fold symmetric assignment. Load the preliminary
          base to see that in action — its eight 45° sectors force an asymmetric 5:3 split.
        </p>
      </>
    ),
    example: 'preliminary-base',
  },
  {
    id: 'local-global',
    title: 'Local vs global flat-foldability',
    body: (
      <>
        <p>
          Kawasaki and Maekawa are <b>local necessary</b> conditions. A pattern where every vertex
          passes both can still fail to fold flat, because the layers have to stack without passing
          through each other.
        </p>
        <p>
          Deciding whether a crease pattern folds flat at all is NP-hard (Bern &amp; Hayes, 1996), so
          fold.app reports what it can actually verify and never claims more.
        </p>
      </>
    ),
  },
  {
    id: 'rigid',
    title: 'Rigid foldability',
    body: (
      <>
        <p>
          A pattern is <b>rigid-foldable</b> if it can fold with every facet staying perfectly flat —
          think sheet metal, not paper. The facets become rigid bodies and the creases become hinges,
          so the sheet is a linkage.
        </p>
        <p>
          Walking once around an interior vertex composes one rotation per crease, and the sheet only
          closes up if that product is the identity. Three scalar equations per interior vertex.
        </p>
      </>
    ),
    example: 'miura-ori',
  },
  {
    id: 'dof',
    title: 'Degrees of freedom',
    body: (
      <>
        <p>
          Subtract the rank of the constraint Jacobian from the number of fold angles and you get the
          dimension of the space of folding motions. The Miura-ori has one: a single slider deploys
          the whole tessellation.
        </p>
        <p>
          The completely flat state is a bifurcation point where several folding branches meet, so
          fold.app measures the rank a short way along the branch instead and says so.
        </p>
      </>
    ),
    example: 'miura-ori',
  },
  {
    id: 'hinge',
    title: 'Hinge angle',
    body: (
      <>
        <p>
          The fold (dihedral) angle of a crease is computed from the two facet normals and the crease
          axis with an <code>atan2</code>, which makes it signed and continuous — except exactly at
          ±180°, where it wraps. Target angles are therefore clamped just short of 180°.
        </p>
      </>
    ),
  },
  {
    id: 'fold-format',
    title: 'The FOLD format',
    body: (
      <>
        <p>
          FOLD is the interchange format for computational origami, designed by Erik Demaine, Jason
          Ku and Robert Lang. It is JSON with parallel arrays: <code>vertices_coords</code>,{' '}
          <code>edges_vertices</code>, <code>edges_assignment</code>, <code>faces_vertices</code> and
          friends.
        </p>
        <p>
          fold.app reads and writes FOLD 1.2, preserving fields it does not understand so that a
          round trip through this app does not destroy another tool's data.
        </p>
      </>
    ),
  },
];

export function LearnPanel(): JSX.Element {
  const [open, setOpen] = useState<string | null>('kawasaki');
  const loadExample = useStore((s) => s.loadExample);
  return (
    <Panel title="Math / Learn" defaultOpen={false}>
      <div className="learn-list">
        {TOPICS.map((t) => (
          <div key={t.id}>
            <button
              aria-pressed={open === t.id}
              onClick={() => setOpen(open === t.id ? null : t.id)}
              style={{ width: '100%' }}
            >
              {t.title}
            </button>
            {open === t.id ? (
              <div style={{ padding: '4px 8px 10px', fontSize: 11.5, lineHeight: 1.55 }}>
                {t.figure ? <div style={{ margin: '4px 0' }}>{t.figure}</div> : null}
                <div className="dim">{t.body}</div>
                {t.example ? (
                  <button
                    className="btn tiny"
                    style={{ marginTop: 6 }}
                    onClick={() => loadExample(t.example as string)}
                  >
                    Show me
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </Panel>
  );
}
